import React, { ReactNode, useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";

interface GradientTextProps {
  children: ReactNode;
  className?: string;
  animated?: boolean;
  colorScheme?: "default" | "cool" | "vibrant" | "cyber" | "brand";
}

export function GradientText({
  children,
  className,
  animated = true,
  colorScheme = "cool",
}: GradientTextProps) {
  // Define different color schemes
  const colorSchemes = {
    default: "from-purple-600 via-purple-500 to-indigo-500",
    cool: "from-cyan-400 via-blue-500 to-purple-600",
    vibrant: "from-pink-500 via-purple-500 to-indigo-500",
    cyber: "from-teal-400 via-blue-500 to-purple-600",
    brand: "from-[#184c06] via-[#1b6504] to-[#229302]",
  };

  const selectedScheme = colorSchemes[colorScheme];
  
  // For static version without animation
  if (!animated) {
    return (
      <span
        className={cn(
          `bg-gradient-to-r ${selectedScheme} bg-clip-text text-transparent inline-block`,
          className
        )}
      >
        {children}
      </span>
    );
  }

  // For animated version
  return (
    <motion.span
      className={cn(
        "relative inline-block",
        className
      )}
      initial={{ opacity: 0.9 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.span
        className={cn(
          `absolute inset-0 bg-gradient-to-r ${selectedScheme} opacity-75 bg-clip-text text-transparent filter blur-[1px]`,
          className
        )}
        animate={{
          backgroundPosition: ["0% 0%", "100% 0%", "0% 0%"],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "linear"
        }}
      >
        {children}
      </motion.span>
      <span className={cn(
        `bg-gradient-to-r ${selectedScheme} bg-clip-text text-transparent z-10 relative`,
        className
      )}>
        {children}
      </span>
    </motion.span>
  );
}